import { Extension } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    paragraphAttrs: {
      /** Toggle `\noindent` — suppresses the first-line indent on this paragraph. */
      toggleNoIndent: () => ReturnType;
      /** Cycle through hanging-indent levels: 0 → 1 → 2 → 0. Maps to `\hangindent`. */
      cycleHangIndent: () => ReturnType;
      /** Toggle drop-cap (lettrine) on the leading paragraph of a section. */
      toggleDropCap: () => ReturnType;
    };
  }
}

/**
 * Per-paragraph layout attributes from the 2026-05-08 brainstorm
 * (`inbox/2026-05-08-formatting-brainstorm-paragraphs-lists.md` §1.2):
 *
 *   - `noIndent` — `\noindent` override on a single paragraph
 *   - `hangIndent` — discrete levels 0/1/2 → `\hangindent=1em` / `2em`
 *   - `dropCap` — `\lettrine{}` styling on the opening paragraph
 *
 * These are paragraph-only (not heading) — alignment lives on text-align.ts
 * and applies to both. LaTeX export wraps with `\noindent`, `\hangindent=…`,
 * and `\lettrine{first-letter}{rest}` respectively on the API side.
 */
export const ParagraphAttrs = Extension.create({
  name: "paragraphAttrs",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph"],
        attributes: {
          noIndent: {
            default: false,
            parseHTML: (el) => el.getAttribute("data-no-indent") === "true",
            renderHTML: (attrs) =>
              attrs.noIndent ? { "data-no-indent": "true", style: "text-indent: 0" } : {},
          },
          hangIndent: {
            default: 0,
            parseHTML: (el) => Number(el.getAttribute("data-hang-indent") || 0),
            renderHTML: (attrs) => {
              const level = Number(attrs.hangIndent || 0);
              if (!level) return {};
              const em = level === 1 ? "1em" : "2em";
              return {
                "data-hang-indent": String(level),
                style: `padding-left: ${em}; text-indent: -${em}`,
              };
            },
          },
          dropCap: {
            default: false,
            parseHTML: (el) => el.getAttribute("data-drop-cap") === "true",
            renderHTML: (attrs) =>
              attrs.dropCap ? { "data-drop-cap": "true", class: "lilia-drop-cap" } : {},
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      toggleNoIndent:
        () =>
        ({ commands, editor }) => {
          const current = !!editor.getAttributes("paragraph").noIndent;
          return commands.updateAttributes("paragraph", { noIndent: !current });
        },
      cycleHangIndent:
        () =>
        ({ commands, editor }) => {
          const current = Number(editor.getAttributes("paragraph").hangIndent || 0);
          const next = (current + 1) % 3;
          return commands.updateAttributes("paragraph", { hangIndent: next });
        },
      toggleDropCap:
        () =>
        ({ commands, editor }) => {
          const current = !!editor.getAttributes("paragraph").dropCap;
          return commands.updateAttributes("paragraph", { dropCap: !current });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Alt-n": () => this.editor.commands.toggleNoIndent(),
      "Mod-Alt-h": () => this.editor.commands.cycleHangIndent(),
    };
  },
});
